import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { BookOpen, Wand2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { errorMessage } from '@/api/errors'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { toast } from '@/components/feedback/toast-store'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { formatDate } from '@/utils/format'
import { classesQuery } from '@/features/academic/api'
import { FormDialog } from '@/features/academic/components/FormDialog'
import { ToggleField } from '@/features/academic/components/ToggleField'
import { useGeneratePapers, useSavePaper } from '../api'
import { paperSchema, type PaperFormInput, type PaperFormOutput } from '../schemas'
import type { Exam, Paper } from '../types'

const PAPER_FORM = 'exam-paper-form'
const PAPER_FIELDS = ['classId', 'subjectId', 'date', 'maxMarks', 'passMarks'] as const

interface PaperDialogProps {
  exam: Exam
  open: boolean
  onOpenChange: (open: boolean) => void
  paper?: Paper | null
  /** Preselected class for a new paper. */
  classId?: number
}

export function PaperDialog({ exam, open, onOpenChange, paper, classId }: PaperDialogProps) {
  const { t } = useTranslation('exams')
  const save = useSavePaper()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<BookOpen />}
      title={paper ? `${paper.subjectName} · ${paper.className}` : t('Add a paper')}
      description={
        paper ? t('Adjust the date and marks of this paper.') : t('Pick a subject from the class curriculum.')
      }
      formId={PAPER_FORM}
      submitLabel={paper ? t('Save paper') : t('Add paper')}
      pending={save.isPending}
    >
      <PaperForm
        exam={exam}
        paper={paper ?? null}
        classId={classId}
        onSubmit={async (values) => {
          const saved = await save.mutateAsync({ ...values, examId: exam.id, id: paper?.id })
          toast.success(
            paper ? t('Paper updated') : t('Paper added'),
            `${saved.subjectName} · ${saved.className}`,
          )
          onOpenChange(false)
        }}
      />
    </FormDialog>
  )
}

function PaperForm({
  exam,
  paper,
  classId,
  onSubmit,
}: {
  exam: Exam
  paper: Paper | null
  classId?: number
  onSubmit: (v: PaperFormOutput) => Promise<void>
}) {
  const { t } = useTranslation('exams')
  const classes = useQuery(classesQuery())
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<PaperFormInput, unknown, PaperFormOutput>({
    resolver: zodResolver(paperSchema),
    defaultValues: {
      classId: paper?.classId ?? classId ?? exam.classes[0]?.id ?? null,
      subjectId: paper?.subjectId ?? null,
      date: paper?.date ?? '',
      maxMarks: String(paper?.maxMarks ?? 100),
      passMarks: String(paper?.passMarks ?? 33),
    },
  })
  const selectedClass = useWatch({ control, name: 'classId' })
  const curriculum = classes.data?.find((c) => c.id === selectedClass)?.subjects ?? []

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, PAPER_FIELDS)
    }
  })

  return (
    <form id={PAPER_FORM} onSubmit={(e) => void submit(e)} noValidate className="grid gap-4 sm:grid-cols-2">
      {paper === null ? (
        <>
          <Controller
            control={control}
            name="classId"
            render={({ field }) => (
              <FormField label={t('Class')} error={errors.classId?.message} required>
                <Select
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                  options={exam.classes.map((c) => ({ value: c.id, label: c.name }))}
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="subjectId"
            render={({ field }) => (
              <FormField label={t('Subject')} error={errors.subjectId?.message} required>
                <Select
                  placeholder={t('Choose…')}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                  options={curriculum.map((s) => ({
                    value: s.subjectId,
                    label: s.isOptional ? t('{{name}} (elective)', { name: s.name }) : s.name,
                  }))}
                />
              </FormField>
            )}
          />
        </>
      ) : null}
      <FormField
        label={t('Date')}
        error={errors.date?.message}
        hint={t('Between {{from}} and {{to}}', {
          from: formatDate(exam.startsOn),
          to: formatDate(exam.endsOn),
        })}
        className="sm:col-span-2"
      >
        <Input type="date" min={exam.startsOn} max={exam.endsOn} {...register('date')} />
      </FormField>
      <FormField label={t('Maximum marks')} error={errors.maxMarks?.message} required>
        <Input inputMode="decimal" className="tabular" {...register('maxMarks')} />
      </FormField>
      <FormField label={t('Pass marks')} error={errors.passMarks?.message} required>
        <Input inputMode="decimal" className="tabular" {...register('passMarks')} />
      </FormField>
    </form>
  )
}

const GENERATE_FORM = 'exam-generate-papers'

/** Creates one paper per curriculum subject for the chosen classes (existing ones are kept). */
export function GeneratePapersDialog({
  exam,
  open,
  onOpenChange,
}: {
  exam: Exam
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('exams')
  const generate = useGeneratePapers()
  const [classIds, setClassIds] = useState<number[]>(exam.classes.map((c) => c.id))
  const [maxMarks, setMaxMarks] = useState('100')
  const [passMarks, setPassMarks] = useState('33')
  const [includeOptional, setIncludeOptional] = useState(true)
  const max = Number(maxMarks)
  const pass = Number(passMarks)
  const invalid = !(max >= 1 && max <= 1000)
    ? 'Enter a maximum between 1 and 1000.'
    : !(pass >= 0 && pass <= max)
      ? 'Pass marks must be between 0 and the maximum.'
      : null

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Wand2 />}
      title={t('Create papers from the curriculum')}
      description={t(
        'One paper per subject of each selected class. Papers that already exist are left untouched.',
      )}
      formId={GENERATE_FORM}
      submitLabel={t('Create papers')}
      pending={generate.isPending}
    >
      <form
        id={GENERATE_FORM}
        noValidate
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (invalid || classIds.length === 0) return
          generate.mutate(
            { examId: exam.id, classIds, maxMarks: max, passMarks: pass, includeOptional },
            {
              onSuccess: (r) => {
                toast.success(
                  r.created ? t('{{count}} papers created', { count: r.created }) : t('Nothing to add'),
                  r.created ? undefined : t('Every curriculum subject already has a paper.'),
                )
                onOpenChange(false)
              },
              onError: (err) => toast.error(t('Could not create papers'), errorMessage(err)),
            },
          )
        }}
      >
        <fieldset>
          <legend className="mb-2 text-[0.8125rem] font-medium text-ink-soft">{t('Classes')}</legend>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {exam.classes.map((c) => (
              <label
                key={c.id}
                className="flex cursor-pointer items-center gap-2.5 rounded-control border border-line px-3 py-2 text-sm text-ink-soft hover:border-line-strong"
              >
                <Checkbox
                  checked={classIds.includes(c.id)}
                  onCheckedChange={(v) =>
                    setClassIds((prev) => (v === true ? [...prev, c.id] : prev.filter((id) => id !== c.id)))
                  }
                />
                {c.name}
              </label>
            ))}
          </div>
          {classIds.length === 0 ? (
            <p className="mt-1.5 text-xs font-medium text-danger-600" role="alert">
              {t('Choose at least one class.')}
            </p>
          ) : null}
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('Maximum marks')}
            error={invalid && !(max >= 1 && max <= 1000) ? invalid : undefined}
          >
            <Input inputMode="decimal" value={maxMarks} onChange={(e) => setMaxMarks(e.target.value)} />
          </FormField>
          <FormField label={t('Pass marks')} error={invalid && max >= 1 && max <= 1000 ? invalid : undefined}>
            <Input inputMode="decimal" value={passMarks} onChange={(e) => setPassMarks(e.target.value)} />
          </FormField>
        </div>
        <ToggleField
          label={t('Include elective subjects')}
          checked={includeOptional}
          onCheckedChange={setIncludeOptional}
        />
      </form>
    </FormDialog>
  )
}
